/**
 * useTasks — the cross-property Tasks backlog store, backed by the worker API
 * (D1 `tasks` table via /app/tasks). Module singleton + useSyncExternalStore so
 * every consumer (the Tasks surface AND the sidebar count badges) stays in sync
 * live. Hydrates once per signed-in user; mutations are optimistic with
 * rollback-by-refetch on failure.
 */

import { useEffect, useSyncExternalStore } from "react"
import { useAuth } from "@/lib/auth"
import { apiClient } from "@/lib/api"

export type TaskStatus = "todo" | "snoozed" | "done"

export interface Task {
  id: string
  /** real property id — CTAs navigate into THIS property's workspace */
  propertyId: string
  propName: string
  source: string
  srcKey: string
  urgent: boolean
  title: string
  detail: string
  cta: string
  /** route section within the task's property workspace */
  ctaSurface: string
  status: TaskStatus
}

let scope = ""
let store: Task[] = []
let hydrating = false
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((l) => l())
}

function normalizeTask(raw: Record<string, unknown>): Task | null {
  const id = typeof raw.id === "string" ? raw.id : ""
  if (!id) return null
  const status = raw.status === "snoozed" || raw.status === "done" ? raw.status : "todo"
  return {
    id,
    propertyId: typeof raw.propertyId === "string" ? raw.propertyId : "",
    propName: typeof raw.propName === "string" && raw.propName ? raw.propName : "Property",
    source: typeof raw.source === "string" ? raw.source : "",
    srcKey: typeof raw.srcKey === "string" ? raw.srcKey : "",
    urgent: raw.urgent === true,
    title: typeof raw.title === "string" ? raw.title : "",
    detail: typeof raw.detail === "string" ? raw.detail : "",
    cta: typeof raw.cta === "string" && raw.cta ? raw.cta : "Open",
    ctaSurface: typeof raw.ctaSurface === "string" ? raw.ctaSurface : "",
    status,
  }
}

async function hydrate(userId: string) {
  if (!userId || hydrating) return
  hydrating = true
  try {
    const res = await apiClient.getTasks()
    if (scope !== userId) return // user changed mid-flight
    store = res.ok && Array.isArray(res.tasks)
      ? res.tasks.map((t) => normalizeTask(t as Record<string, unknown>)).filter((t): t is Task => t !== null)
      : []
    notify()
  } catch {
    if (scope === userId) {
      store = []
      notify()
    }
  } finally {
    hydrating = false
  }
}

function ensureScope(userId: string) {
  if (!userId || scope === userId) return
  scope = userId
  store = []
  notify()
  void hydrate(userId)
}

function setStore(next: Task[]) {
  store = next
  notify()
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

function getSnapshot() {
  return store
}

function setStatus(id: string, status: TaskStatus) {
  const prevScope = scope
  setStore(store.map((t) => (t.id === id ? { ...t, status } : t)))
  void apiClient.updateTaskStatus(id, status).then((res) => {
    if (!res.ok && scope === prevScope) void hydrate(prevScope)
  }).catch(() => {
    if (scope === prevScope) void hydrate(prevScope)
  })
}

export interface TasksApi {
  tasks: Task[]
  counts: { todo: number; snoozed: number; done: number }
  markDone: (id: string) => void
  snooze: (id: string) => void
  resume: (id: string) => void
  addTask: (task: Task) => void
}

export function useTasks(): TasksApi {
  const { user } = useAuth()
  const userId = user?.id || ""

  useEffect(() => {
    ensureScope(userId)
  }, [userId])

  const tasks = useSyncExternalStore(subscribe, getSnapshot)
  return {
    tasks,
    counts: {
      todo: tasks.filter((t) => t.status === "todo").length,
      snoozed: tasks.filter((t) => t.status === "snoozed").length,
      done: tasks.filter((t) => t.status === "done").length,
    },
    markDone: (id) => setStatus(id, "done"),
    snooze: (id) => setStatus(id, "snoozed"),
    resume: (id) => setStatus(id, "todo"),
    addTask: (task) => {
      if (store.some((t) => t.id === task.id)) return
      const prevScope = scope
      setStore([task, ...store])
      void apiClient.createTask({
        id: task.id,
        propertyId: task.propertyId,
        source: task.source,
        srcKey: task.srcKey,
        urgent: task.urgent,
        title: task.title,
        detail: task.detail,
        cta: task.cta,
        ctaSurface: task.ctaSurface,
        status: task.status,
      }).then((res) => {
        if (!res.ok && scope === prevScope) void hydrate(prevScope)
      }).catch(() => {
        if (scope === prevScope) void hydrate(prevScope)
      })
    },
  }
}
